import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PromotionPolicyToken } from '@sleed/bizcommerce-api';
import { promotionPoliciesApi } from '~/lib/api/client';
import { formToToken } from '~/lib/mappers/promotion';
import { promotionKeys } from '~/hooks/use-promotion';
import type { PromotionFormValues } from '~/lib/schemas/promotion';

/**
 * Create / update / soft-delete plus two client-side workarounds for endpoints the
 * generated client does not expose (verified — see task-analysis/admin-redesign-phase4):
 *  - duplicate: fetch → strip identity → create (`_COPY` code suffix).
 *  - reorder: persist each affected policy's Priority (Σειρά) via N× update. Racy
 *    at scale; a backend bulk-reorder endpoint is the proper fix (flagged to user).
 */
export function usePromotionMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: promotionKeys.all });

	const create = useMutation({
		mutationFn: async (form: PromotionFormValues) => (await promotionPoliciesApi.create(formToToken(form))).data,
		onSuccess: invalidate
	});

	const update = useMutation({
		mutationFn: async (form: PromotionFormValues) => {
			const token = formToToken(form);
			return (await promotionPoliciesApi.update(token, token.id)).data;
		},
		onSuccess: invalidate
	});

	const remove = useMutation({
		mutationFn: async (id: number) => (await promotionPoliciesApi.remove(id)).data,
		onSuccess: invalidate
	});

	const duplicate = useMutation({
		mutationFn: async (id: number) => {
			const { data: source } = await promotionPoliciesApi.getById(id);
			const clone: PromotionPolicyToken = {
				...source,
				id: undefined,
				SchemaVersion: 2, // parity with formToToken; Create rejects a missing/stale version
				Code: source.Code ? `${source.Code}_COPY` : undefined
			};
			return (await promotionPoliciesApi.create(clone)).data;
		},
		onSuccess: invalidate
	});

	const reorder = useMutation({
		mutationFn: async (ordered: PromotionPolicyToken[]) => {
			await Promise.all(ordered.map((token, index) => promotionPoliciesApi.update({ ...token, Priority: index + 1 }, token.id)));
		},
		onSuccess: invalidate
	});

	return { create, update, remove, duplicate, reorder };
}
